/**
 * BulkUpdateDialog — Team Members bulk update modal
 *
 * Allows updating Company Access and Approval Workflow for multiple
 * selected employees at once.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Building2,
  GitBranch,
  Loader2,
  Users,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import * as integrationsService from '@/services/integrationsRedesignService';
import type { EmployeeCompanyAccessRow } from '@/types';
import { getStoredWorkflows } from '@/utils/workflowStorage';
import type { ApprovalWorkflow as StoredApprovalWorkflow } from '@/types/workflow';
import { useScreenDataMode } from '@/hooks/useScreenDataMode';
import * as workflowAssignmentService from '@/services/workflowAssignmentService';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BulkEmployee {
  id: string;
  name: string;
}

interface CompanyUpdate {
  companyId: string;
  action: 'grant' | 'revoke' | 'no-change';
  setAsDefault?: boolean;
}

interface WorkflowOption {
  id: string;
  name: string;
  description: string;
  totalStages?: number;
  totalApprovers?: number;
  isDefault?: boolean;
}

interface BulkUpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEmployees: BulkEmployee[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const BulkUpdateDialog: React.FC<BulkUpdateDialogProps> = ({
  open,
  onOpenChange,
  selectedEmployees,
}) => {
  const { mode } = useScreenDataMode('team-members');

  // --- Company Access state ---
  const [companyRows, setCompanyRows] = useState<EmployeeCompanyAccessRow[]>([]);
  const [companyUpdates, setCompanyUpdates] = useState<Record<string, 'grant' | 'revoke' | 'no-change'>>({});
  const [defaultCompanyId, setDefaultCompanyId] = useState<string | null>(null);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  // --- Approval Workflow state ---
  const [workflows, setWorkflows] = useState<WorkflowOption[]>([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('NO_CHANGE');
  const [isLoadingWorkflows, setIsLoadingWorkflows] = useState(false);

  // --- Submit state ---
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('company-access');

  // ---------------------------------------------------------------------------
  // Load data when dialog opens
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!open) return;
    setActiveTab('company-access');
    setCompanyUpdates({});
    setDefaultCompanyId(null);
    setSelectedWorkflowId('NO_CHANGE');
    loadCompanies();
    loadWorkflows();
  }, [open]);

  const loadCompanies = async () => {
    setIsLoadingCompanies(true);
    try {
      // Load company list from first employee as reference (same companies apply to all)
      const firstId = selectedEmployees[0]?.id;
      if (!firstId) return;
      const data = await integrationsService.getEmployeeCompanyAccess(firstId);
      setCompanyRows(data);
      // Default: no-change for all
      const initialUpdates: Record<string, 'grant' | 'revoke' | 'no-change'> = {};
      data.forEach((r) => { initialUpdates[r.companyId] = 'no-change'; });
      setCompanyUpdates(initialUpdates);
    } catch {
      toast.error('Failed to load companies');
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const loadWorkflows = async () => {
    setIsLoadingWorkflows(true);
    try {
      if (mode === 'demo') {
        await new Promise((r) => setTimeout(r, 400));
        const storedWorkflows = getStoredWorkflows();

        const converted: WorkflowOption[] = storedWorkflows
          .filter((w: StoredApprovalWorkflow) => w.isActive)
          .map((w: StoredApprovalWorkflow) => ({
            id: w.id,
            name: w.name,
            description: w.description || '',
            totalStages: w.stages?.length ?? 0,
            totalApprovers: w.stages?.reduce((s, st) => s + (st.approvers?.length ?? 0), 0) ?? 0,
            isDefault: w.isDefault,
          }));

        if (converted.length === 0) {
          setWorkflows([
            {
              id: 'default-workflow',
              name: 'Standard Approval Workflow',
              description: 'Standard two-stage approval process',
              totalStages: 2,
              totalApprovers: 3,
              isDefault: true,
            },
            {
              id: 'executive-workflow',
              name: 'Executive Approval Workflow',
              description: 'Multi-stage approval for executives',
              totalStages: 4,
              totalApprovers: 6,
              isDefault: false,
            },
            {
              id: 'fast-track-workflow',
              name: 'Fast Track Approval',
              description: 'Single-stage approval for trusted employees',
              totalStages: 1,
              totalApprovers: 1,
              isDefault: false,
            },
          ]);
        } else {
          setWorkflows(converted);
        }
      } else {
        const data = await workflowAssignmentService.getAllWorkflows(true);
        setWorkflows(
          data.map((w) => ({
            id: w.id,
            name: w.name,
            description: w.description || '',
            totalStages: w.totalStages,
            totalApprovers: w.totalApprovers,
            isDefault: w.isDefault,
          }))
        );
      }
    } catch {
      if (mode === 'live') toast.error('Failed to load workflows');
    } finally {
      setIsLoadingWorkflows(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const changedCompanies = Object.entries(companyUpdates).filter(([, v]) => v !== 'no-change');
  const hasCompanyChanges = changedCompanies.length > 0;
  const hasWorkflowChange = selectedWorkflowId !== 'NO_CHANGE';
  const hasAnyChange = hasCompanyChanges || hasWorkflowChange;

  const cycleCompanyAction = (companyId: string) => {
    setCompanyUpdates((prev) => {
      const current = prev[companyId] ?? 'no-change';
      const next =
        current === 'no-change' ? 'grant' : current === 'grant' ? 'revoke' : 'no-change';
      return { ...prev, [companyId]: next };
    });
  };

  const actionLabel = (action: 'grant' | 'revoke' | 'no-change') => {
    if (action === 'grant') return { label: 'Grant', cls: 'bg-success-50 text-success-700 border-success-200 dark:bg-success-950/30 dark:text-success-400' };
    if (action === 'revoke') return { label: 'Revoke', cls: 'bg-error-50 text-error-700 border-error-200 dark:bg-error-950/30 dark:text-error-400' };
    return { label: 'No change', cls: 'bg-muted text-muted-foreground border-border' };
  };

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = async () => {
    if (!hasAnyChange) return;
    setIsSubmitting(true);
    try {
      const parts: string[] = [];
      const failures: string[] = [];

      // Apply company access changes for each employee sequentially
      if (hasCompanyChanges) {
        const grants = changedCompanies.filter(([, v]) => v === 'grant');
        const revokes = changedCompanies.filter(([, v]) => v === 'revoke');
        let companySuccessCount = 0;

        if (mode === 'live') {
          for (const emp of selectedEmployees) {
            try {
              // Fetch this employee's current access so we use the correct baseline
              const empAccess = await integrationsService.getEmployeeCompanyAccess(emp.id);
              const empAccessMap = new Map(empAccess.map((r) => [r.companyId, r]));

              const accessUpdates = companyRows.map((row) => {
                const action = companyUpdates[row.companyId] ?? 'no-change';
                const existing = empAccessMap.get(row.companyId);
                const currentHasAccess = existing?.hasAccess ?? false;
                const currentIsDefault = existing?.isDefault ?? false;

                return {
                  companyId: row.companyId,
                  hasAccess:
                    action === 'grant' ? true : action === 'revoke' ? false : currentHasAccess,
                  // Only override isDefault when the user explicitly picked a default;
                  // otherwise preserve the employee's existing default.
                  isDefault:
                    defaultCompanyId != null
                      ? defaultCompanyId === row.companyId
                      : currentIsDefault,
                };
              });

              await integrationsService.updateEmployeeCompanyAccess(emp.id, accessUpdates);
              companySuccessCount++;
            } catch {
              failures.push(`${emp.name}: company access update failed`);
            }
          }
        } else {
          await new Promise((r) => setTimeout(r, 400));
          companySuccessCount = selectedEmployees.length;
        }

        if (grants.length) parts.push(`${grants.length} compan${grants.length === 1 ? 'y' : 'ies'} granted`);
        if (revokes.length) parts.push(`${revokes.length} compan${revokes.length === 1 ? 'y' : 'ies'} revoked`);
        if (companySuccessCount < selectedEmployees.length) {
          parts.push(`${companySuccessCount}/${selectedEmployees.length} succeeded`);
        }
      }

      // Apply workflow assignment change
      if (hasWorkflowChange) {
        const wf = workflows.find((w) => w.id === selectedWorkflowId);

        if (mode === 'live') {
          const result = await workflowAssignmentService.bulkAssignWorkflow({
            employeeIds: selectedEmployees.map((emp) => parseInt(emp.id, 10)),
            workflowId: selectedWorkflowId,
            reason: 'Bulk update from Team Members page',
          });
          if (result.failedCount > 0) {
            failures.push(`${result.failedCount} workflow assignment(s) failed`);
          }
        } else {
          await new Promise((r) => setTimeout(r, 400));
        }

        parts.push(`workflow set to "${wf?.name ?? selectedWorkflowId}"`);
      }

      if (failures.length > 0) {
        toast.warning(
          `Partially updated ${selectedEmployees.length} employee${selectedEmployees.length === 1 ? '' : 's'}`,
          { description: [...parts, ...failures].join(' • ') }
        );
      } else {
        toast.success(
          `Updated ${selectedEmployees.length} employee${selectedEmployees.length === 1 ? '' : 's'}`,
          { description: parts.join(' • ') }
        );
      }
      onOpenChange(false);
    } catch {
      toast.error('Failed to apply bulk updates');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] h-[90vh] max-h-[720px] flex flex-col gap-0 p-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-base">Bulk Update</DialogTitle>
              <DialogDescription className="text-sm mt-0.5">
                Applying changes to{' '}
                <span className="font-semibold text-foreground">
                  {selectedEmployees.length} employee{selectedEmployees.length === 1 ? '' : 's'}
                </span>
              </DialogDescription>
            </div>
          </div>

          {/* Selected employees pills */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {selectedEmployees.slice(0, 6).map((emp) => (
              <Badge key={emp.id} variant="secondary" className="text-xs font-normal">
                {emp.name}
              </Badge>
            ))}
            {selectedEmployees.length > 6 && (
              <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                +{selectedEmployees.length - 6} more
              </Badge>
            )}
          </div>
        </DialogHeader>

        {/* Body */}
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex flex-col flex-1 min-h-0 overflow-hidden"
        >
          <TabsList className="w-full rounded-none border-b bg-transparent px-6 justify-start h-11 gap-4">
            <TabsTrigger
              value="company-access"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 h-full text-sm"
            >
              <Building2 className="h-4 w-4 mr-1.5" />
              Company Access
              {hasCompanyChanges && (
                <span className="ml-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                  {changedCompanies.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="approval-workflow"
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 h-full text-sm"
            >
              <GitBranch className="h-4 w-4 mr-1.5" />
              Approval Workflow
              {hasWorkflowChange && (
                <span className="ml-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-semibold text-primary-foreground">
                  1
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── Company Access Tab ── */}
          <TabsContent value="company-access" className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden">
            <div className="h-full overflow-y-auto px-6 py-4">
              {isLoadingCompanies ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-8 rounded-md" />
                        <div className="space-y-1">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-16" />
                        </div>
                      </div>
                      <Skeleton className="h-7 w-24 rounded-full" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-1">
                  <Alert className="mb-3 border-info-200 bg-info-50 dark:bg-info-950/20">
                    <AlertDescription className="text-xs text-info-700 dark:text-info-400">
                      Click a company row to cycle through: <strong>No change</strong> → <strong>Grant</strong> → <strong>Revoke</strong>
                    </AlertDescription>
                  </Alert>

                  {companyRows.map((row) => {
                    const update = companyUpdates[row.companyId] ?? 'no-change';
                    const { label, cls } = actionLabel(update);
                    return (
                      <button
                        key={row.companyId}
                        type="button"
                        onClick={() => cycleCompanyAction(row.companyId)}
                        className={cn(
                          'w-full flex items-center justify-between py-2.5 px-3 rounded-lg transition-all text-left',
                          'hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                          update !== 'no-change' && 'ring-1 ring-inset ring-border bg-muted/30'
                        )}
                      >
                        {/* Left: company info */}
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-muted flex-shrink-0">
                            <Building2 className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{row.companyName}</p>
                            <p className="text-xs text-muted-foreground">{row.companyCode}</p>
                          </div>
                        </div>

                        {/* Right: action badge */}
                        <Badge
                          variant="outline"
                          className={cn('text-xs font-medium flex-shrink-0', cls)}
                        >
                          {update === 'grant' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                          {update === 'revoke' && <AlertTriangle className="h-3 w-3 mr-1" />}
                          {label}
                        </Badge>
                      </button>
                    );
                  })}

                  {/* Default company selector — only show when there are grant actions */}
                  {companyRows.some((r) => companyUpdates[r.companyId] === 'grant') && (
                    <div className="mt-4 pt-3 border-t">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        Set default company (optional)
                      </p>
                      <div className="space-y-1">
                        {companyRows
                          .filter((r) => companyUpdates[r.companyId] === 'grant')
                          .map((row) => (
                            <label
                              key={row.companyId}
                              className="flex items-center gap-2.5 py-2 px-3 rounded-lg hover:bg-muted/40 cursor-pointer"
                            >
                              <Checkbox
                                checked={defaultCompanyId === row.companyId}
                                onCheckedChange={(checked) =>
                                  setDefaultCompanyId(checked ? row.companyId : null)
                                }
                              />
                              <span className="text-sm">{row.companyName}</span>
                            </label>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── Approval Workflow Tab ── */}
          <TabsContent value="approval-workflow" className="flex-1 min-h-0 mt-0 data-[state=inactive]:hidden">
            <div className="h-full overflow-y-auto px-6 py-4">
              {isLoadingWorkflows ? (
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 rounded-lg border">
                      <Skeleton className="h-4 w-4 rounded-full mt-0.5 flex-shrink-0" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-56" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <RadioGroup
                  value={selectedWorkflowId}
                  onValueChange={setSelectedWorkflowId}
                  className="space-y-2"
                >
                  {/* No change option */}
                  <label
                    htmlFor="wf-NO_CHANGE"
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                      selectedWorkflowId === 'NO_CHANGE'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/40'
                    )}
                  >
                    <RadioGroupItem value="NO_CHANGE" id="wf-NO_CHANGE" className="mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">No change</p>
                      <p className="text-xs text-muted-foreground">
                        Keep each employee's current workflow assignment
                      </p>
                    </div>
                  </label>

                  {/* Available workflows */}
                  {workflows.map((wf) => (
                    <label
                      key={wf.id}
                      htmlFor={`wf-${wf.id}`}
                      className={cn(
                        'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                        selectedWorkflowId === wf.id
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/40'
                      )}
                    >
                      <RadioGroupItem value={wf.id} id={`wf-${wf.id}`} className="mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium">{wf.name}</p>
                          {wf.isDefault && (
                            <Badge variant="secondary" className="text-xs py-0 h-4">
                              Default
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{wf.description}</p>
                        {(wf.totalStages !== undefined || wf.totalApprovers !== undefined) && (
                          <div className="flex items-center gap-3 mt-1.5">
                            {wf.totalStages !== undefined && (
                              <span className="text-xs text-muted-foreground">
                                {wf.totalStages} stage{wf.totalStages !== 1 ? 's' : ''}
                              </span>
                            )}
                            {wf.totalApprovers !== undefined && (
                              <span className="text-xs text-muted-foreground">
                                {wf.totalApprovers} approver{wf.totalApprovers !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}

                  {/* No approval option */}
                  <label
                    htmlFor="wf-NO_APPROVAL"
                    className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all',
                      selectedWorkflowId === 'NO_APPROVAL'
                        ? 'border-warning-400 bg-warning-50 dark:bg-warning-950/20'
                        : 'border-border hover:bg-muted/40'
                    )}
                  >
                    <RadioGroupItem value="NO_APPROVAL" id="wf-NO_APPROVAL" className="mt-0.5" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">No Approval Required</p>
                        <Badge
                          variant="outline"
                          className="text-xs py-0 h-4 border-warning-300 text-warning-700"
                        >
                          Caution
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Timesheets auto-approve upon submission
                      </p>
                    </div>
                  </label>
                </RadioGroup>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t bg-muted/30 gap-2">
          <div className="flex-1 text-xs text-muted-foreground">
            {hasAnyChange ? (
              <span className="flex items-center gap-1.5 text-foreground font-medium">
                <CheckCircle2 className="h-3.5 w-3.5 text-success-600" />
                {[
                  hasCompanyChanges && `${changedCompanies.length} company change${changedCompanies.length !== 1 ? 's' : ''}`,
                  hasWorkflowChange && 'workflow change',
                ]
                  .filter(Boolean)
                  .join(' + ')}{' '}
                ready
              </span>
            ) : (
              <span>No changes selected</span>
            )}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!hasAnyChange || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Applying…
              </>
            ) : (
              <>Apply to {selectedEmployees.length} employee{selectedEmployees.length !== 1 ? 's' : ''}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
